import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import styles from './statusCard.module.css';
import draftsIcon from '../../assets/icons/drafts.png';
import enplanificationIcon from '../../assets/icons/enplanification.png';
import attenteIcon from '../../assets/icons/attente.png';
import entraitementIcon from '../../assets/icons/entraitement.png';
import doneIcon from '../../assets/icons/done.png';
import enproduction from '../../assets/icons/enproduction.png';
import archiveIcon from '../../assets/icons/archive.png';
import { collection, query, where, getDocs, onSnapshot, getCountFromServer } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { auth, db } from '../../firebase/firebase';
import { onAuthStateChanged } from 'firebase/auth';

const StatusCard = () => {
  const [userName, setUserName] = useState('');
  const [loading, setLoading] = useState(true);
  const [bannerUrl, setBannerUrl] = useState('');
  const [counts, setCounts] = useState({
    drafts: 0,
    enplanification: 0,
    attente: 0,
    entraitement: 0,
    enproduction: 0,
    termine: 0
  });
  
  const navigate = useNavigate();
  const location = useLocation();
  
  // Cards data
  const cards = [
    { icon: draftsIcon, number: counts.drafts, label: 'Brouillons', path: '/brouillons' },
    { icon: enplanificationIcon, number: counts.enplanification, label: 'En planification', path: '/enplanification' },
    { icon: attenteIcon, number: counts.attente, label: 'En attente de validation', path: '/enattente' },
    { icon: enproduction, number: counts.enproduction, label: 'En production', path: '/enproduction' },
    { icon: entraitementIcon, number: counts.entraitement, label: 'En traitement', path: '/entraitement' },
    { icon: doneIcon, number: counts.termine, label: 'Terminé(s)', path: '/termine' }
  ];

  useEffect(() => {
    let isMounted = true;
    let unsubscribeFunctions = [];
    
    const setupCountsListener = (userId) => {
      const statusMap = {
        0: 'drafts',
        1: 'enplanification',
        2: 'attente',
        3: 'enproduction',
        4: 'entraitement',
        5: 'termine'
      };
      
      // Set up listeners for each status
      Object.keys(statusMap).forEach((status) => {
        const patientsRef = collection(db, 'patients');
        const q = query(
          patientsRef,
          where('userId', '==', userId),
          where('status', '==', parseInt(status))
        );
        
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          if (isMounted) {
            // Filter out archived patients
            const nonArchivedCount = querySnapshot.docs.filter(doc => {
              const data = doc.data();
              return data.archived !== 1;
            }).length;

            setCounts(prev => ({
              ...prev,
              [statusMap[status]]: nonArchivedCount
            }));
          }
        }, (error) => {
          /*console.error(`Error listening to status ${status}:`, error);*/
        });

        unsubscribeFunctions.push(unsubscribe);
      });
    };

    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      // Clean up previous listeners
      unsubscribeFunctions.forEach(unsubscribe => unsubscribe());
      unsubscribeFunctions = [];

      if (user) {
        setupCountsListener(user.uid);
      } else {
        setCounts({
          drafts: 0,
          enplanification: 0,
          attente: 0,
          entraitement: 0,
          enproduction: 0,
          termine: 0
        });
      }
    });

    return () => {
      isMounted = false;
      unsubscribeAuth();
      // Clean up all listeners
      unsubscribeFunctions.forEach(unsubscribe => unsubscribe());
    };
  }, []);

  const handleCardClick = (path, index) => {
    navigate(path);
  };

  // Function to check if a card is selected based on the current URL path
  const isCardSelected = (cardPath) => {
    return location.pathname === cardPath;
  };
  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true);
      try {
        const user = auth.currentUser;
        
        if (user) {
          // Get the user's email and convert to lowercase for comparison
          const userEmail = user.email.toLowerCase();
          
          // Try to find the user with exact email match first
          const medecinsRef = collection(db, 'medecins'); 
          const q = query(medecinsRef, where('email', '==', user.email)); 
          let querySnapshot = await getDocs(q);
          
          // If no match, try with lowercase email
          if (querySnapshot.empty) {
            // Get all medecins and filter manually since Firestore doesn't support case-insensitive queries
            const allMedecinsQuery = query(medecinsRef);
            const allMedecinsSnapshot = await getDocs(allMedecinsQuery);
            
            // Find a document with case-insensitive email match
            const matchingDocs = allMedecinsSnapshot.docs.filter(doc => {
              const docEmail = doc.data().email;
              return docEmail && docEmail.toLowerCase() === userEmail;
            });
            
            if (matchingDocs.length > 0) {
              const userData = matchingDocs[0].data();
              setUserName(userData.name || '');
            } else {
              setUserName(user.displayName || 'Utilisateur');
            }
          } else {
            const userData = querySnapshot.docs[0].data();
            setUserName(userData.name || '');
          }
        } else {
          setUserName('Utilisateur');
        }
      } catch (error) {
        /*console.error('Error fetching user data:', error);*/
        setUserName('Utilisateur');
      } finally {
        setLoading(false);
      }
    };

    // Call fetchUserData immediately
    fetchUserData();

    // Set up auth state listener
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        fetchUserData();
      } else {
        setUserName('Utilisateur');
      }
    });

    return () => {
      unsubscribeAuth();
    };
  }, []);

  useEffect(() => {
    const fetchBannerImage = async () => {
      try {
        const storage = getStorage();
        const bannerRef = ref(storage, 'pub/banner');
        const url = await getDownloadURL(bannerRef);
        setBannerUrl(url);
      } catch (error) {
        /*console.error('Error fetching banner image:', error);*/
      }
    };

    fetchBannerImage();
  }, []);

  return (
    <>
      <div className={styles.welcomeSection}>
                <p>Bonjour, <br /><span className={styles.doctorName}>Dr {userName || 'Utilisateur'}</span></p>
        {bannerUrl && (
          <div className={styles.banner}>
            <img src={bannerUrl} alt="Practice Performance Banner" />
          </div>
        )}
      </div>
      <div className={styles.statusCards}>
        {cards.map((card, index) => (
          <div 
            key={index} 
            className={`${styles.statusCard} ${isCardSelected(card.path) ? styles.selected : ''}`}
            onClick={() => handleCardClick(card.path, index)}
          >
            <img src={card.icon} alt={card.label} />
            <div className={styles.group}>
              <div className={styles.statusNumber}>{card.number}</div>
              <div className={styles.statusLabel}>{card.label}</div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default StatusCard;